'use client';

import React, { useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';
import { camelizeKeys } from 'humps';
import { TrainingUGCHeader } from './components/TrainingUGCHeader';
import { Link } from '../../../../components/Link';
import { UGCBlock } from '../../../../components/UGCBlock';
import { VirtualList } from '../../../../components/VirtualList';
import { VirtualItem } from '../../../../components/VirtualList/components/VirtualItem';
import { WagContentApiEndpoint, WAG_CONTENT_API_URL } from '../../../../constants/endpoints';
import { HtmlDomId } from '../../../../constants/app';
import { TrainingGuideUGCResponse, CamelizedTrainingGuideUGCResponse } from '../../../../types/api/trainingGuide';
import { CamelizedTrainingQuestion, CamelizedTrainingSuccessStory } from '../../../../types/app/training-guide';
import questionIcon from '../../../../assets/icons/ugc-question.svg';
import storyIcon from '../../../../assets/icons/ugc-success-story.svg';
import { applyPathParams } from '../../../../wag-react/common/api/utils/applyPathParams';
import { httpGet } from '../../../../wag-react/common/api/api';
import { Box } from '../../../../wag-react/next-components/box';
import { H2 } from '../../../../wag-react/core-components/h2';
import { theme } from '../../../../wag-react/themes/base-theme';
import { Button } from '../../../../wag-react/next-components/button';
export type TrainingUGCSectionProps = {
  animalTypeSlug: string;
  nextPage: number;
  nextPageUrl: string;
  questions: CamelizedTrainingQuestion[];
  slug: string;
  successStories: CamelizedTrainingSuccessStory[];
};
type TrainingUGCItem = CamelizedTrainingQuestion | CamelizedTrainingSuccessStory;
export const TrainingUGCSection = ({
  animalTypeSlug,
  nextPage: initialNextPage,
  nextPageUrl,
  questions,
  slug,
  successStories
}: TrainingUGCSectionProps) => {
  const [ugcNextPage, setUgcNextPage] = useState(initialNextPage);

  // Typing on state needed here to successfully call .ratingLikes later,
  // since that doesn't exist on a CamelizedTrainingSuccessStory type
  const [ugcItems, setUgcItems] = useState<TrainingUGCItem[]>([...questions, ...successStories]);

  // Performs API request to wagcontent to fetch next
  // page of UGC
  const getNextUGC = async () => {
    const endpoint = applyPathParams(WagContentApiEndpoint.GetTrainingGuideUGC)({
      animalTypeSlug,
      slug
    });
    const response = await httpGet<TrainingGuideUGCResponse>(endpoint, {
      baseURL: WAG_CONTENT_API_URL,
      params: {
        page: ugcNextPage
      }
    });
    const camelizedResponse = camelizeKeys(response.data) as CamelizedTrainingGuideUGCResponse;
    setUgcItems([...ugcItems, ...camelizedResponse.questions, ...camelizedResponse.successStories]);
    setUgcNextPage(camelizedResponse.nextPage);
  };
  const parentRef = useRef(undefined);
  const rowVirtualizer = useVirtual({
    size: ugcItems.length,
    parentRef
  });
  return <Box id={HtmlDomId.ArticleCommentsSection} data-sentry-element="Box" data-sentry-component="TrainingUGCSection" data-sentry-source-file="index.tsx">

      {ugcItems.length > 0 && <>
          <H2 color={theme.colors.dimGray} fontFamily="muliBold" fontSize="desktop-h3" my="20px" px={['16px', 0]}>
            Training Questions and Answers
          </H2>

          <VirtualList items={ugcItems} itemHeight={250} parentRef={parentRef} virtualizer={rowVirtualizer}>
            {rowVirtualizer.virtualItems.map(virtualItem => {
          const item = ugcItems[virtualItem.index];
          const ugcTypeLabelProps = {
            alt: 'Success story icon',
            icon: storyIcon,
            type: 'Success'
          };

          // If item object has a ratingLikes property,
          // it is considered a Question
          if ('ratingLikes' in item) {
            ugcTypeLabelProps.alt = 'Question icon';
            ugcTypeLabelProps.icon = questionIcon;
            ugcTypeLabelProps.type = 'Question';
          }
          return <VirtualItem virtualRow={virtualItem} key={virtualItem.index}>
                  <UGCBlock HeaderComponent={<TrainingUGCHeader age={item.age} breed={item.breed} name={item.petName} {...'ratingLikes' in item && {
              numLikes: item.ratingLikes
            }} ugcTypeLabel={ugcTypeLabelProps} />} ugcItem={item} mb="28px" />
                </VirtualItem>;
        })}
          </VirtualList>

          <Box textAlign="center" mt="10px">
            <Button display={ugcNextPage ? 'inline-block' : 'none'} onClick={getNextUGC} variant="contained">
              See more
            </Button>

            {/* Hidden Link pointing to next page URL, for SEO purposes */}
            {nextPageUrl && <Link href={nextPageUrl} style={{
          position: 'absolute',
          left: '-1000px',
          top: '-1000px',
          visibility: 'hidden',
          pointerEvents: 'none'
        }}>
                See more
              </Link>}
          </Box>
        </>}
    </Box>;
};